import type { ErpAiLessonReviewQueueQuery } from 'gqlhooks';

// Карточка урока (Zoom) — одна функция на весь ИИ: живёт в модели анализа
// занятий, очередь проверки («Jakość») берёт её оттуда.
export { aiLessonCardHref } from '../AiLessons/aiLessonModel';

/**
 * ТЗ 0104, §5 / §7 — чистая модель вкладки «Jakość»: метрики качества из
 * `aiAgentOverview.agents[].metrics` (точность находок Slack, использование
 * черновиков ответов) и очередь проверки анализов занятий.
 *
 * Без React — тестируется напрямую; импортируется прямым путём
 * (`core/hooks/AiAgents/aiQualityModel`), не через барабан `core/hooks`.
 */

export type AiLessonReviewRow = ErpAiLessonReviewQueueQuery['aiLessonAnalyses']['rows'][number];

/** Ключи агентов, чьи метрики показывает вкладка. */
export const AI_QUALITY_SLACK_AGENT = 'slack_watch';
export const AI_QUALITY_REPLY_AGENT = 'reply_draft';

// ── Разбор `metrics` (JSONString) ──────────────────────────────────────────

export interface IAiMetricsObject {
  /** Сервер не смог посчитать метрики (`{"error": "metrics_failed"}`). */
  failed: boolean;
  values: Record<string, unknown>;
}

/** `metrics` — строка с JSON-объектом (или уже объект); мусор — пусто. */
export const parseAiMetricsObject = (raw: unknown): IAiMetricsObject => {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return { failed: false, values: {} };
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { failed: false, values: {} };
  }
  const values = value as Record<string, unknown>;
  return { failed: typeof values.error === 'string', values };
};

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Наблюдатель Slack: точность «Nie to» и счётчики отзывов за месяц. */
export interface IAiSlackQuality {
  failed: boolean;
  /** 1 − false_positive / с отзывом; отзывов нет — `null`. */
  precision: number | null;
  findings: number | null;
  withFeedback: number | null;
  confirmed: number | null;
  falsePositive: number | null;
  tasksCreated: number | null;
}

export const aiSlackQuality = (raw: unknown): IAiSlackQuality => {
  const { failed, values } = parseAiMetricsObject(raw);
  return {
    failed,
    precision: num(values.precision),
    findings: num(values.findings),
    withFeedback: num(values.with_feedback),
    confirmed: num(values.confirmed),
    falsePositive: num(values.false_positive),
    tasksCreated: num(values.tasks_created),
  };
};

export interface IAiRejectReasonCount {
  reason: string;
  count: number;
}

/** Черновики ответов: доли вставленных и отправленных, правка, отказы. */
export interface IAiReplyQuality {
  failed: boolean;
  proposed: number | null;
  insertRate: number | null;
  sentRate: number | null;
  /** Медиана нормализованного расстояния правки 0…1 (0 — как есть). */
  medianEditDistance: number | null;
  noReplyNeededShare: number | null;
  rejectReasons: IAiRejectReasonCount[];
}

export const aiReplyQuality = (raw: unknown): IAiReplyQuality => {
  const { failed, values } = parseAiMetricsObject(raw);
  const reasons = Array.isArray(values.reject_reasons)
    ? (values.reject_reasons as unknown[])
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const row = item as Record<string, unknown>;
          const count = num(row.count);
          const reason = typeof row.reason === 'string' ? row.reason : '';
          return count === null ? null : { reason, count };
        })
        .filter((item): item is IAiRejectReasonCount => item !== null)
    : [];
  return {
    failed,
    proposed: num(values.proposed),
    insertRate: num(values.insert_rate),
    sentRate: num(values.sent_rate),
    medianEditDistance: num(values.median_edit_distance),
    noReplyNeededShare: num(values.no_reply_needed_share),
    rejectReasons: reasons,
  };
};

// ── Числа ──────────────────────────────────────────────────────────────────

const PERCENT_FMT = new Intl.NumberFormat('pl-PL', {
  style: 'percent',
  maximumFractionDigits: 1,
});
const SCORE_FMT = new Intl.NumberFormat('pl-PL', {
  maximumFractionDigits: 1,
});

/** Доля 0…1 → «82,5%»; нет — «—». Польская запись при любом языке. */
export const formatAiShare = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : PERCENT_FMT.format(value);

/** Балл анализа занятия 0…100 → «72,5»; нет — «—». */
export const formatAiScore = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : SCORE_FMT.format(value);

/**
 * Тон точности (`DS.StatValue`): ниже 75 % — жёлтый (модель ошибается чаще,
 * чем каждый четвёртый раз), ниже 50 % — красный, иначе обычный.
 */
export const aiPrecisionTone = (
  value: number | null,
): 'plain' | 'warn' | 'danger' => {
  if (value === null) return 'plain';
  if (value < 0.5) return 'danger';
  if (value < 0.75) return 'warn';
  return 'plain';
};

// ── Очередь проверки анализов занятий ──────────────────────────────────────

/** Статус проверки «ждёт методиста». */
export const AI_LESSON_REVIEW_OPEN = 'open';

/** Строк в очереди проверки. */
export const AI_LESSON_REVIEW_PAGE_SIZE = 50;
